import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export const GallerySection = ({ images = [], loading = false }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section
      id="gallery"
      className="section-darker py-24 md:py-32"
      data-testid="gallery-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-gold/80 font-manrope text-sm tracking-[0.3em] uppercase mb-4">
            Moments Captured
          </p>
          <h2 className="font-cinzel text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Photo <span className="text-gold">Gallery</span>
          </h2>
          <div className="section-divider mx-auto" />
        </motion.div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="gallery-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="gallery-item">
                <Skeleton className="w-full h-full bg-white/10" />
              </div>
            ))}
          </div>
        ) : Array.isArray(images) && images.length > 0 ? (
          <div className="gallery-grid">
            {images.map((image, index) => (
              <motion.div
                key={image.id || index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="gallery-item cursor-pointer"
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={image.url || ""}
                  alt={image.alt || `Gallery item ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="gallery-overlay flex items-end p-4">
                  <span className="text-white text-sm font-manrope">
                    {image.alt || ""}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-white/40 text-center">No gallery items available.</p>
        )}

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center text-white/40 font-manrope text-sm mt-8"
        >
          More photos from performances and classes coming soon
        </motion.p>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>

            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage.url || ""}
              alt={selectedImage.alt || ""}
              className="max-w-full max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GallerySection;
